import React from "react";

interface IFaqGroupHeadingProps {
  /** Heading level — set by the surface so the outline never skips a level. */
  as: "h2" | "h3";
  label: string;
  count: number;
  id?: string;
  /**
   * Supply both to make the heading a collapse toggle. Omit them and it stays
   * a plain label, as on surfaces where the questions are always visible.
   */
  open?: boolean;
  onToggle?: () => void;
  /** Id of the panel the toggle controls. */
  controls?: string;
}

/**
 * Category label with a rule line and question count, optionally acting as the
 * collapse toggle for the questions beneath it.
 *
 * Sized `text-base md:text-lg font-semibold` — the site-wide h3 treatment
 * (matching "About this home" on the listing page), so the FAQ blocks read at
 * the same level as neighbouring sections.
 */
const FaqGroupHeading: React.FC<IFaqGroupHeadingProps> = ({
  as,
  label,
  count,
  id,
  open,
  onToggle,
  controls,
}) => {
  const Heading = as;

  const inner = (
    <>
      <Heading
        id={id}
        className="m-0 text-base font-semibold text-ink-black [text-wrap:pretty] md:text-lg"
      >
        {label}
      </Heading>
      <span aria-hidden="true" className="h-px flex-1 bg-neutral-200" />
      <span className="font-mono-blog text-11 text-ink-muted md:text-11">
        {count}
      </span>
    </>
  );

  if (!onToggle) {
    return (
      <div className="flex items-center gap-3 pb-0.5 md:gap-3.5 md:pb-1">
        {inner}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      className="flex w-full cursor-pointer items-center gap-3 border-none bg-transparent pb-0.5 text-left text-inherit md:gap-3.5 md:pb-1"
    >
      {inner}
      {/* Same chevron treatment as the /faq section headings. */}
      <span
        aria-hidden="true"
        className={`faq-icon flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full text-11 leading-none ${
          open
            ? "rotate-180 bg-neutral-100 text-brand-600"
            : "rotate-0 bg-neutral-100 text-ink-muted"
        }`}
      >
        ▼
      </span>
    </button>
  );
};

export default FaqGroupHeading;
